import './Navbar.sass';

import React, { FC, useMemo } from 'react';
import { Link, useRoute } from 'react-router5';
import { useDispatch, useSelector } from 'react-redux';

import { NavbarItemProps, NavbarProps } from '$shared/components/Navbar/Navbar.constant';
import {
  ADMIN,
  ADMIN_ACTIONS_STATS,
  ADMIN_ADS,
  ADMIN_BATTLE,
  ADMIN_BROADCASTS,
  ADMIN_FINANCE,
  ADMIN_MESSAGES,
  ADMIN_CHATS,
  ADMIN_MINE_EXPLORE_CARDS,
  ADMIN_MODEL_ADS,
  ADMIN_MODERATION,
  ADMIN_MODERATION_VIEW,
  ADMIN_MODERATIONS,
  ADMIN_MODERATIONS_LIST,
  ADMIN_MODERATION_STATS,
  ADMIN_MODERATION_QUEUE,
  ADMIN_MODERATION_LOGS,
  ADMIN_PROMO_CODES,
  ADMIN_REPORTS,
  ADMIN_SPAM_CASES,
  ADMIN_SPAM_CASE,
  ADMIN_TRANSACTIONS,
  ADMIN_TRANSLATIONS,
  ADMIN_AUDIT_LOG,
  ADMIN_USERS,
  ADMIN_UTM_MODEL_LIST,
  ADMIN_WITHDRAWALS,
  AUTH_REQUIRED,
  DASHBOARD,
  FEED,
  GROUPS,
  IM,
  LISTS,
  NOTIFY,
  OTHER,
  PROFILE,
  REELS,
  RESOLVE,
  SEARCH,
  STORAGE,
} from '$shared/constants/pages';
import { useCurrentLang, useCurrentUserInfo, useTranslator } from '$hooks';
import { classNames, getDocumentHref, isChristmasHolidays, isIsolated } from '$utils';
import { ModalLink } from '$shared/components';
import {
  IcAds28,
  IcBell24,
  IcBubble24,
  IcBurger24,
  IcClips24,
  IcContest28,
  IcDashboard24,
  IcDollar24,
  IcGift28,
  IcGlobe24,
  IcHome24,
  IcList28,
  IcLogo40,
  IcMessage28,
  IcPayouts24,
  IcPickaxe28,
  IcReport24,
  IcSearch24,
  IcTranslate28,
  IcUser28,
  IcUsers24,
  IcWrite28,
} from '$assets';
import { contentPrioritySelector, mainSelector, permissionsSelector } from '$store/main';
import { Badge } from '$uikit';
import { ContentPriorityType } from '$store/main/interface';
import { useAdminPanel } from '$hooks/useAdminPanel';
import { feedActions } from '$store/feed';
import { authIsLoggedSelector } from '$store/auth';

const NavbarItem: FC<NavbarItemProps> = (props) => {
  const {
    children,
    routeName,
    routeParams,
    isActive = false,
    isModal = false,
    icon,
    counter = 0,
    badge = false,
    visible = true,
  } = props;

  const dispatch = useDispatch();

  const handleClick = () => {
    if (isActive) {
      const scrollNode = document.querySelector('._ScrollView');

      if (scrollNode) {
        if (routeName === STORAGE) {
          scrollNode.scrollTo({ top: scrollNode.scrollHeight, behavior: 'smooth' });
          return;
        }

        scrollNode.scrollTo({ top: 0, behavior: 'smooth' });
      }
    } else if (routeName === FEED) {
      dispatch(feedActions.clearFeed());
    }
  };

  let Component = Link;
  if (isModal) {
    Component = ModalLink;
  }

  if (!visible) {
    return null;
  }

  return (
    <Component
      className={classNames({
        NavbarItem: true,
        active: isActive,
      })}
      routeName={routeName}
      routeParams={routeParams}
      onClick={handleClick}
    >
      <div className="NavbarItem__icon">{icon}</div>
      <div className="NavbarItem__label">{children}</div>
      {counter > 0 && (
        <div className="NavbarItem__counter">
          <Badge count={counter} />
        </div>
      )}
      {badge && <div className="NavbarItem__badge" />}
    </Component>
  );
};

export const Navbar: FC<NavbarProps> = () => {
  const user = useCurrentUserInfo();
  const { route } = useRoute();
  const currentLang = useCurrentLang();
  const adminPanel = useAdminPanel();
  const { newDialogs, hasNotify, accounts, mVerified } = useSelector(mainSelector);
  const t = useTranslator();
  const contentPriority = useSelector(contentPrioritySelector);
  const permissions = useSelector(permissionsSelector);
  const isLogged = useSelector(authIsLoggedSelector);
  const isolated = isIsolated() || user?.isolated;
  const isChristmas = isChristmasHolidays();

  const hasOtherBadge = useMemo(() => {
    if (!user) {
      return false;
    }

    for (let account of accounts) {
      if (account.updatesCount > 0 && account.id !== user.id) {
        return true;
      }
    }
    return false;
  }, [accounts, user]);

  if (!isLogged) {
    if (isolated) {
      return null;
    }

    return (
      <div className="Navbar">
        <NavbarItem
          routeName={REELS}
          isActive={route?.name === REELS}
          icon={<IcClips24 />}
        >
          {t('navbar_reels')}
        </NavbarItem>
        <NavbarItem
          routeName={SEARCH}
          isActive={route?.name === SEARCH}
          icon={<IcSearch24 />}
        >
          {t('navbar_search')}
        </NavbarItem>
        <NavbarItem
          routeName={AUTH_REQUIRED}
          isActive={route?.name === AUTH_REQUIRED}
          icon={<IcUser28 />}
        >
          {t('tabbar_profile')}
        </NavbarItem>
      </div>
    );
  }

  return (
    <div className="Navbar">
      <Link routeName={adminPanel ? ADMIN : FEED} className="Navbar__logo">
        <IcLogo40 />
        {window.isMedia ? 'TonPlace.io' : 'Ton.Place'}
      </Link>
      {!adminPanel ? (
        <>
          {mVerified && (
            <NavbarItem
              routeName={DASHBOARD}
              isActive={route?.name === DASHBOARD}
              icon={<IcDashboard24 />}
            >
              {t('navbar_dashboard')}
            </NavbarItem>
          )}
          {(contentPriority === ContentPriorityType.OTHER || mVerified) && (
            <NavbarItem
              routeName={user.domain ? RESOLVE : PROFILE}
              routeParams={user.domain ? { domain: user.domain } : { id: user.id }}
              isActive={route?.name === PROFILE && route?.params?.id === user.id}
              icon={<IcUser28 />}
            >
              {t('navbar_my_page')}
            </NavbarItem>
          )}
          <NavbarItem
            routeName={FEED}
            isActive={route?.name === FEED}
            icon={<IcHome24 />}
          >
            {t('navbar_news')}
          </NavbarItem>
          {!isolated && contentPriority === ContentPriorityType.ADULT && (
            <NavbarItem
              routeName={REELS}
              isActive={route?.name === REELS}
              icon={<IcClips24 />}
            >
              {t('navbar_reels')}
            </NavbarItem>
          )}
          <NavbarItem
            routeName={IM}
            isActive={route?.name === IM}
            icon={<IcBubble24 />}
            counter={newDialogs}
          >
            {t('navbar_im')}
          </NavbarItem>
          <NavbarItem
            routeName={NOTIFY}
            isActive={route?.name === NOTIFY}
            icon={<IcBell24 />}
            badge={hasNotify}
          >
            {t('navbar_notify')}
          </NavbarItem>
          {contentPriority !== ContentPriorityType.ADULT && (
            <NavbarItem
              routeName={GROUPS}
              isActive={route?.name === GROUPS}
              icon={<IcUsers24 />}
            >
              {t('navbar_groups')}
            </NavbarItem>
          )}
          {!isolated && (
            <NavbarItem
              routeName={SEARCH}
              isActive={route?.name === SEARCH}
              icon={<IcSearch24 />}
            >
              {t('navbar_search')}
            </NavbarItem>
          )}
          {/*<NavbarItem*/}
          {/*  routeName={FIND_FRIENDS}*/}
          {/*  isActive={route?.name === FIND_FRIENDS}*/}
          {/*  icon={<IcUserPlus28 />}*/}
          {/*>{t('navbar_find_friends')}</NavbarItem>*/}
          <NavbarItem
            routeName={STORAGE}
            isActive={route?.name === STORAGE}
            icon={<IcDollar24 />}
          >
            {t('purchased_content')}
          </NavbarItem>
          {mVerified && (
            <NavbarItem
              routeName={LISTS}
              isActive={route?.name === LISTS}
              icon={<IcList28 />}
            >
              {t('lists_title')}
            </NavbarItem>
          )}
          <NavbarItem
            routeName={OTHER}
            isActive={route?.name === OTHER}
            icon={
              accounts.length > 1 ? (
                <div
                  className="TabbarItem__user_photo"
                  style={{ backgroundImage: `url(${user.photo})` }}
                />
              ) : (
                <IcBurger24 />
              )
            }
            badge={hasOtherBadge}
          >
            {t('navbar_more')}
          </NavbarItem>
        </>
      ) : (
        <>
          {/*<NavbarItem*/}
          {/*  routeName={ADMIN}*/}
          {/*  isActive={route?.name === ADMIN}*/}
          {/*  icon={<IcHome24 />}*/}
          {/*>Dashboard</NavbarItem>*/}
          {/*<NavbarItem*/}
          {/*  routeName={ADMIN_FINANCE}*/}
          {/*  isActive={route?.name === ADMIN_FINANCE}*/}
          {/*  icon={<IcPayouts24 />}*/}
          {/*>Finance</NavbarItem>*/}
          <NavbarItem
            visible={permissions.users}
            routeName={ADMIN_USERS}
            isActive={route?.name === ADMIN_USERS}
            icon={<IcUsers24 />}
          >
            Users
          </NavbarItem>
          <NavbarItem
            visible={permissions.report}
            routeName={ADMIN_REPORTS}
            isActive={route?.name === ADMIN_REPORTS}
            icon={<IcReport24 />}
          >
            Reports
          </NavbarItem>
          <NavbarItem
            visible={permissions.report}
            routeName={ADMIN_SPAM_CASES}
            isActive={route?.name === ADMIN_SPAM_CASES || route?.name === ADMIN_SPAM_CASE}
            icon={<IcReport24 />}
          >
            Spam
          </NavbarItem>
          <NavbarItem
            visible={permissions.report}
            routeName={ADMIN_MODERATIONS}
            isActive={
              route?.name === ADMIN_MODERATIONS ||
              route?.name === ADMIN_MODERATIONS_LIST ||
              route?.name === ADMIN_MODERATION_STATS ||
              route?.name === ADMIN_MODERATION_QUEUE ||
              route?.name === ADMIN_MODERATION ||
              route?.name === ADMIN_MODERATION_VIEW ||
              route?.name === ADMIN_MODERATION_LOGS
            }
            icon={<IcReport24 />}
          >
            Moderation
          </NavbarItem>
          <NavbarItem
            visible={permissions.battle}
            routeName={ADMIN_MINE_EXPLORE_CARDS}
            isActive={route?.name === ADMIN_MINE_EXPLORE_CARDS}
            icon={<IcPickaxe28 />}
          >
            Mining Cards
          </NavbarItem>
          <NavbarItem
            visible={permissions.finance}
            routeName={ADMIN_FINANCE}
            isActive={route?.name === ADMIN_FINANCE}
            icon={<IcPayouts24 />}
          >
            Finance
          </NavbarItem>
          <NavbarItem
            visible={permissions.withdrawal}
            routeName={ADMIN_WITHDRAWALS}
            isActive={route?.name === ADMIN_WITHDRAWALS}
            icon={<IcPayouts24 />}
          >
            Withdrawals
          </NavbarItem>
          <NavbarItem
            routeName={ADMIN_ADS}
            isActive={route?.name === ADMIN_ADS}
            icon={<IcAds28 />}
          >
            Ads
          </NavbarItem>
          <NavbarItem
            visible={permissions.utm}
            routeName={ADMIN_UTM_MODEL_LIST}
            isActive={route?.name === ADMIN_UTM_MODEL_LIST}
            icon={<IcGlobe24 />}
          >
            Utm
          </NavbarItem>
          <NavbarItem
            visible={permissions.utm} // UTM IT'S TRUE FOR THIS SECTION
            routeName={ADMIN_PROMO_CODES}
            isActive={route?.name === ADMIN_PROMO_CODES}
            icon={<IcGift28 />}
          >
            Promo codes
          </NavbarItem>
          <NavbarItem
            visible={permissions.utm}
            routeName={ADMIN_MODEL_ADS}
            isActive={route?.name === ADMIN_MODEL_ADS}
            icon={<IcAds28 />}
          >
            Model Ads
          </NavbarItem>
          <NavbarItem
            visible={permissions.finance}
            routeName={ADMIN_ACTIONS_STATS}
            isActive={route?.name === ADMIN_ACTIONS_STATS}
            icon={<IcAds28 />}
          >
            Actions Stats
          </NavbarItem>
          <NavbarItem
            visible={permissions.messages}
            routeName={ADMIN_CHATS}
            isActive={route?.name === ADMIN_CHATS}
            icon={<IcBubble24 />}
          >
            Chats
          </NavbarItem>
          <NavbarItem
            visible={permissions.messages}
            routeName={ADMIN_MESSAGES}
            isActive={route?.name === ADMIN_MESSAGES}
            icon={<IcMessage28 />}
          >
            Messages
          </NavbarItem>
          <NavbarItem
            visible={permissions.messages}
            routeName={ADMIN_BROADCASTS}
            isActive={route?.name === ADMIN_BROADCASTS}
            icon={<IcWrite28 />}
          >
            Broadcasts
          </NavbarItem>
          <NavbarItem
            visible={permissions.translation}
            routeName={ADMIN_TRANSLATIONS}
            isActive={route?.name === ADMIN_TRANSLATIONS}
            icon={<IcTranslate28 />}
          >
            Translations
          </NavbarItem>
          <NavbarItem
            visible={permissions.finance}
            routeName={ADMIN_TRANSACTIONS}
            isActive={route?.name === ADMIN_TRANSACTIONS}
            icon={<IcUsers24 />} // TODO: Поменять иконку
          >
            Transactions
          </NavbarItem>
          <NavbarItem
            visible={permissions.battle}
            routeName={ADMIN_BATTLE}
            isActive={route?.name === ADMIN_BATTLE}
            icon={<IcContest28 />}
          >
            Battle
          </NavbarItem>
          <NavbarItem
            visible={permissions.auditLog}
            routeName={ADMIN_AUDIT_LOG}
            isActive={route?.name === ADMIN_AUDIT_LOG}
            icon={<IcList28 />}
          >
            Audit Log
          </NavbarItem>
        </>
      )}
      <div className="Navbar__links">
        <a href="https://wiki.ton.place" target="_blank" rel="noreferrer">
          {t('other_support')}
        </a>
        <a href={getDocumentHref('terms')} target="_blank" rel="noreferrer">
          {t('navbar_links_terms')}
        </a>
        <a href="/privacy.html" target="_blank">
          {t('navbar_links_privacy')}
        </a>
        {/*<Link routeName={LANGUAGE}>{currentLang.label}</Link>*/}
      </div>
    </div>
  );
};
